import { Input } from '@mlm/ui';
import { hexToHslTriplet, hslTripletToHex } from '@mlm/shared';

/**
 * One theme colour: a swatch picker, the raw HSL triplet, and a reset.
 *
 * Shared by Layout & Theme (the storefront palette) and Learning Pages → Look
 * (the academy palette). `value` undefined means "not overridden" — the field
 * shows the baseline as a placeholder and the app falls through to its
 * stylesheet, which is what keeps an un-themed tenant looking like the demo.
 */
export function ColorField({
  label,
  value,
  baseline,
  onChange,
}: {
  label: string;
  value: string | undefined;
  baseline?: string;
  onChange: (v: string | undefined) => void;
}) {
  const effective = value ?? baseline;
  const hex = effective ? hslTripletToHex(effective) : '#cccccc';
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={hex}
        onChange={(e) => onChange(hexToHslTriplet(e.target.value))}
        className="h-9 w-10 shrink-0 cursor-pointer rounded border"
        aria-label={label}
        title={label}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium">{label}</div>
        <Input
          value={value ?? ''}
          placeholder={baseline ? `${baseline} · baseline` : 'unset → baseline'}
          onChange={(e) => onChange(e.target.value.trim() === '' ? undefined : e.target.value)}
          className="mt-0.5 h-7 font-mono text-[11px]"
        />
      </div>
      {value !== undefined && (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          className="shrink-0 text-[11px] text-muted-foreground hover:text-foreground"
          title="Reset to baseline"
        >
          reset
        </button>
      )}
    </div>
  );
}
